import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, q, explain } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAsync } from '../lib/useAsync';
import { useGroup } from './GroupLayout';
import { balanceTeamsWithPositions, effectiveRating, pairKey } from '../lib/balance';
import { teamsAsText, copyText } from '../lib/teamText';
import { formLabel } from '../lib/form';
import { EmptyState, ErrorBanner, Spinner } from '../components/Shared';
import Pitch from '../components/Pitch';
import PositionPickerModal from '../components/PositionPickerModal';
import Icon from '../components/Icon';

function AttendeeSelector({ members, selected, toggle, setAll, onGenerate }) {
  const count = Object.values(selected).filter(Boolean).length;
  return (
    <>
      <header className="page__head">
        <div>
          <h1 className="page__title">MATCHDAY</h1>
          <p className="page__sub">Pick who's playing today</p>
        </div>
        {members.length > 0 && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAll(count !== members.length)}>
            {count === members.length ? 'Clear all' : 'Select all'}
          </button>
        )}
      </header>

      {members.length === 0 ? (
        <EmptyState text="Add players to your squad first." />
      ) : (
        <div className="attendees">
          {members.map((p) => {
            const active = !!selected[p.id];
            const form = formLabel(p.form);
            return (
              <button
                type="button"
                key={p.id}
                className={`attendee ${active ? 'attendee--on' : ''}`}
                onClick={() => toggle(p.id)}
                aria-pressed={active}
              >
                <span className="attendee__mark">
                  {active ? <Icon name="check" size={16} /> : effectiveRating(p).toFixed(1)}
                </span>
                <span className="attendee__body">
                  <span className="attendee__nameline">
                    <span className="attendee__name">{p.name}</span>
                    {form && <span className={`chip chip--${form.tone}`}>{form.text}</span>}
                  </span>
                  <span className="attendee__pos">
                    {p.positions?.length ? p.positions.join(' / ') : 'No position set'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {count >= 2 && (
        <div className="sticky-action">
          <button type="button" className="btn btn--primary btn--block btn--lg" onClick={onGenerate}>
            <Icon name="shuffle" size={18} />
            Balance {count} players
          </button>
        </div>
      )}
      {count === 1 && <p className="hint hint--center">Pick at least two players.</p>}
    </>
  );
}

// How far back the balancer looks when rotating the keeper and breaking up
// habitual pairings. About two months of a weekly game — long enough to spot a
// pattern, short enough that last spring doesn't dictate tonight.
const RECENT_GAMES = 8;

export default function BalanceScreen() {
  const { group, isAdmin } = useGroup();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);
  const [savedGameId, setSavedGameId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [editingPositionFor, setEditingPositionFor] = useState(null);
  const [copied, setCopied] = useState(false);

  const { data, loading, error, reload } = useAsync(async () => {
    const rows = await q(
      supabase
        .from('group_members')
        .select('id, profile_id, role, positions, attributes, form, rating_offset, display_name, profiles(display_name)')
        .eq('group_id', group.id),
      'Loading the squad'
    );
    const members = (rows || [])
      .map((m) => ({
        ...m,
        name: (m.profile_id ? m.profiles?.display_name : null) || m.display_name || 'Unnamed',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Recent games feed two fairnesses the ratings are blind to: who keeps
    // getting stuck in goal, and who always ends up on the same side as whom.
    // Best-effort — a split is still worth having if this can't be read.
    const history = { gkCount: {}, pairCount: {}, games: 0 };
    const { data: recentGames } = await supabase
      .from('games')
      .select('id')
      .eq('group_id', group.id)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(RECENT_GAMES);

    if (recentGames?.length) {
      const { data: parts } = await supabase
        .from('game_participants')
        .select('game_id, member_id, team, position')
        .in('game_id', recentGames.map((g) => g.id));

      history.games = recentGames.length;
      const byGame = {};
      (parts || []).forEach((p) => {
        if (p.position === 'GK') history.gkCount[p.member_id] = (history.gkCount[p.member_id] || 0) + 1;
        (byGame[`${p.game_id}|${p.team}`] ||= []).push(p.member_id);
      });
      Object.values(byGame).forEach((side) => {
        for (let i = 0; i < side.length; i++) {
          for (let j = i + 1; j < side.length; j++) {
            const k = pairKey(side[i], side[j]);
            history.pairCount[k] = (history.pairCount[k] || 0) + 1;
          }
        }
      });
    }

    return { members, history };
  }, [group.id]);

  const members = data?.members || [];
  const history = data?.history || null;
  const attendees = useMemo(() => members.filter((p) => selected[p.id]), [members, selected]);
  const manualEdits = result ? [...result.teamA, ...result.teamB].filter((p) => p.isManualOverride).length : 0;

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  const setAll = (on) => setSelected(on ? Object.fromEntries(members.map((m) => [m.id, true])) : {});

  const generate = (isReshuffle) => {
    if (isReshuffle && manualEdits > 0) {
      const ok = window.confirm(
        `Reshuffling will undo your ${manualEdits} manual position change${manualEdits === 1 ? '' : 's'}. Continue?`
      );
      if (!ok) return;
    }
    const pool = result ? [...result.teamA, ...result.teamB] : attendees;
    setResult(balanceTeamsWithPositions(pool, history));
    setSaved(false);
    setSaveError('');
  };

  const reset = () => {
    setResult(null);
    setSavedGameId(null);
    setSaved(false);
    setSaveError('');
    setSelected({});
  };

  const applyPositionChange = (newPosition) => {
    const { team, player } = editingPositionFor;
    const key = team === 'A' ? 'teamA' : 'teamB';
    setResult((prev) => ({
      ...prev,
      [key]: prev[key].map((p) =>
        p.id === player.id ? { ...p, assignedPosition: newPosition, isManualOverride: true } : p
      ),
    }));
    setEditingPositionFor(null);
    setSaved(false);
  };

  // Two players trade sides AND positions. Moving one across on their own would
  // leave the teams uneven and one formation a player short, so a swap is the
  // only manual change that keeps both sheets valid.
  const applySwap = (otherId) => {
    const { team, player } = editingPositionFor;
    const fromKey = team === 'A' ? 'teamA' : 'teamB';
    const toKey = team === 'A' ? 'teamB' : 'teamA';

    setResult((prev) => {
      const other = prev[toKey].find((p) => p.id === otherId);
      if (!other) return prev;
      const leaving = { ...player, assignedPosition: other.assignedPosition, isManualOverride: true };
      const arriving = { ...other, assignedPosition: player.assignedPosition, isManualOverride: true };
      return {
        ...prev,
        [fromKey]: prev[fromKey].map((p) => (p.id === player.id ? arriving : p)),
        [toKey]: prev[toKey].map((p) => (p.id === other.id ? leaving : p)),
      };
    });
    setEditingPositionFor(null);
    setSaved(false);
  };

  const save = async () => {
    if (!result || !isAdmin) return;
    setSaving(true);
    setSaveError('');
    try {
      let gameId = savedGameId;

      if (!gameId) {
        // Reuse an already-balanced game for this group rather than stacking up
        // duplicates — the Log screen only ever picks the newest one, so extras
        // would silently strand results.
        const pending = await q(
          supabase
            .from('games')
            .select('id')
            .eq('group_id', group.id)
            .eq('status', 'balanced')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          'Checking for a game in progress'
        );
        if (pending) {
          gameId = pending.id;
        } else {
          const newGame = await q(
            supabase
              .from('games')
              .insert({ group_id: group.id, created_by: user.id, status: 'balanced' })
              .select()
              .single(),
            'Creating the game'
          );
          gameId = newGame.id;
        }
      }

      const rows = [
        ...result.teamA.map((p) => ({ member_id: p.id, team: 'A', position: p.assignedPosition, is_manual_override: !!p.isManualOverride })),
        ...result.teamB.map((p) => ({ member_id: p.id, team: 'B', position: p.assignedPosition, is_manual_override: !!p.isManualOverride })),
      ].map((r) => ({ ...r, game_id: gameId }));

      // Drop anyone who was in a previous save of this game but isn't playing
      // now, otherwise they'd linger on the Log screen as a ghost.
      const existing = await q(
        supabase.from('game_participants').select('id, member_id').eq('game_id', gameId),
        'Checking who was already saved'
      );
      const keeping = new Set(rows.map((r) => r.member_id));
      const stale = (existing || []).filter((p) => !keeping.has(p.member_id)).map((p) => p.id);
      if (stale.length) {
        await q(
          supabase.from('game_participants').delete().in('id', stale),
          'Clearing players who dropped out'
        );
      }

      await q(
        supabase.from('game_participants').upsert(rows, { onConflict: 'game_id,member_id' }),
        'Saving the teams'
      );

      setSavedGameId(gameId);
      setSaved(true);
    } catch (err) {
      setSaveError(explain(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><Spinner label="Loading the squad…" /></div>;
  if (error) return <div className="page"><ErrorBanner error={error} onRetry={reload} /></div>;

  if (!result) {
    return (
      <div className="page">
        <AttendeeSelector
          members={members}
          selected={selected}
          toggle={toggle}
          setAll={setAll}
          onGenerate={() => generate(false)}
        />
      </div>
    );
  }

  const { teamA, teamB } = result;
  const avgA = teamA.reduce((s, p) => s + p._rating, 0) / (teamA.length || 1);
  const avgB = teamB.reduce((s, p) => s + p._rating, 0) / (teamB.length || 1);
  const gap = Math.abs(avgA - avgB);

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">THE SPLIT</h1>
          <p className="page__sub">
            {teamA.length + teamB.length} players · rating gap{' '}
            <strong className={gap < 0.15 ? 'gap gap--tight' : gap < 0.4 ? 'gap' : 'gap gap--wide'}>
              {gap.toFixed(2)}
            </strong>
          </p>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={reset}>
          Start over
        </button>
      </header>

      {isAdmin && (
        <p className="hint">
          Click any player on the pitch to change their position, or swap them with someone from the
          other side.
        </p>
      )}
      {result.keeperless && (
        <p className="hint">
          {result.keepersAvailable === 1
            ? "Only one player tonight lists goalkeeper, so both sides are all outfield rather than one having a keeper and the other not — sort it out between you."
            : "Nobody tonight lists goalkeeper, so everyone's outfield. Rotate in goal between you as usual."}
        </p>
      )}
      {history?.games > 0 && (
        <p className="hint">
          Balanced against the last {history.games} game{history.games === 1 ? '' : 's'} too — rotating who
          goes in goal, and splitting up players who keep ending up on the same side.
        </p>
      )}
      {saveError && <ErrorBanner error={saveError} />}

      <div className="pitches">
        {[
          { label: 'TEAM A', players: teamA, avg: avgA, variant: 'a', key: 'A' },
          { label: 'TEAM B', players: teamB, avg: avgB, variant: 'b', key: 'B' },
        ].map((t) => (
          <section key={t.key} className="pitch-block">
            <div className="pitch-block__head">
              <h2 className={`pitch-block__label pitch-block__label--${t.variant}`}>{t.label}</h2>
              <span className="pitch-block__avg">{t.avg.toFixed(1)} avg</span>
            </div>
            <Pitch
              players={t.players.map((p) => ({
                ...p,
                _form: formLabel(p.form)?.tone || null,
              }))}
              variant={t.variant}
              onPlayerClick={isAdmin ? (p) => setEditingPositionFor({ team: t.key, player: p }) : null}
            />
          </section>
        ))}
      </div>

      <div className="row row--gap row--actions">
        <button type="button" className="btn btn--outline" onClick={() => generate(true)}>
          <Icon name="shuffle" size={15} />
          Reshuffle
        </button>
        <button
          type="button"
          className="btn btn--outline"
          onClick={async () => {
            const ok = await copyText(
              teamsAsText({ teamA, teamB, groupName: group.name, date: Date.now() })
            );
            setCopied(ok);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          <Icon name={copied ? 'check' : 'copy'} size={15} />
          {copied ? 'Copied' : 'Copy'}
        </button>
        {isAdmin ? (
          <button
            type="button"
            className={`btn ${saved ? 'btn--saved' : 'btn--primary'}`}
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save teams'}
          </button>
        ) : (
          <span className="btn btn--disabled">Only admins can save</span>
        )}
      </div>

      {saved && (
        <div className="banner banner--good">
          <Icon name="check" size={18} className="banner__icon" />
          <div className="banner__body">
            <p className="banner__title">Teams saved</p>
            <p className="banner__detail banner__detail--plain">Log the result on the Log tab once you've played.</p>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate(`/g/${group.id}/log`)}>
            Go to Log
          </button>
        </div>
      )}

      {editingPositionFor && (
        <PositionPickerModal
          player={editingPositionFor.player}
          otherTeam={editingPositionFor.team === 'A' ? result.teamB : result.teamA}
          otherLabel={editingPositionFor.team === 'A' ? 'Team B' : 'Team A'}
          onSelect={applyPositionChange}
          onSwap={applySwap}
          onCancel={() => setEditingPositionFor(null)}
        />
      )}
    </div>
  );
}
